import React, { useCallback, useEffect, useState } from "react";
import { Form, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import * as inventoryService from "../../services/inventoryService";
import ExportMenu from "../../components/InventoryExport/ExportMenu";
import InventoryTable from "./InventoryTable";
import { formatReportDate } from "../../utils/dateFormat";
import { formatDecimal } from "../../utils/numberFormat";
import { getVendorCurrency } from "../../utils/currency";

const tabBtnStyle = (active) => ({
  padding: "9px 18px",
  borderRadius: "9px",
  border: "none",
  background: active ? "var(--button-color)" : "transparent",
  color: active ? "#fff" : "#64748B",
  fontWeight: active ? "600" : "500",
  fontSize: "13px",
  cursor: "pointer",
  transition: "all 0.15s",
  whiteSpace: "nowrap",
  boxShadow: active ? "0 2px 8px rgba(201,25,74,0.25)" : "none",
});

const VARIANCE_BADGE = {
  MISSING_TXN: "danger",
  QTY_MISMATCH: "warning",
  ORPHANED_TXN: "secondary",
  PENDING_COMPLETION: "info",
};

const VARIANCE_LABEL = {
  MISSING_TXN: "Missing Deduction",
  QTY_MISMATCH: "Qty Mismatch",
  ORPHANED_TXN: "Orphaned Entry",
  PENDING_COMPLETION: "Pending Completion",
};

// Six reports amendment: Stock movement, Stock by performed-by ("technician"
// in the original ask - there is no technician entity in this app, see
// README-inventory-module.md), Low stock alerts, Material consumption per
// period, Inventory variance (booking_parts vs ledger reconciliation),
// Adjustment logs. "Stock by warehouse" was explicitly dropped - no
// warehouse/branch entity exists in v1.
const REPORTS = [
  {
    key: "stock-movement",
    label: "Stock Movement",
    dateFiltered: true,
    columns: [
      { key: "created_at", label: "Date", format: "date", render: (row) => formatReportDate(row.created_at) },
      { key: "item_name", label: "Item" },
      { key: "sku", label: "SKU" },
      { key: "txn_type", label: "Type" },
      { key: "qty_change", label: "Qty change", format: "number", render: (row) => formatDecimal(row.qty_change) },
      { key: "unit_cost", label: "Unit cost", format: "currency", render: (row) => `${getVendorCurrency()} ${formatDecimal(row.unit_cost)}` },
      { key: "balance_after", label: "Balance after", format: "number", render: (row) => formatDecimal(row.balance_after) },
      { key: "reason", label: "Reason" },
      { key: "performed_by_name", label: "Performed by" },
    ],
  },
  {
    key: "by-performed-by",
    label: "Stock by User",
    dateFiltered: true,
    columns: [
      { key: "performed_by_name", label: "User" },
      { key: "performed_by_email", label: "Email" },
      { key: "txn_count", label: "Transactions" },
      { key: "total_qty", label: "Total qty", format: "number", render: (row) => formatDecimal(row.total_qty) },
      { key: "total_cost", label: "Total cost", format: "currency", render: (row) => `${getVendorCurrency()} ${formatDecimal(row.total_cost)}` },
    ],
  },
  {
    key: "low-stock",
    label: "Low Stock Alerts",
    dateFiltered: false,
    columns: [
      { key: "name", label: "Item" },
      { key: "sku", label: "SKU" },
      { key: "current_qty", label: "Current qty", format: "number", render: (row) => formatDecimal(row.current_qty) },
      { key: "reorder_level", label: "Reorder level", format: "number", render: (row) => formatDecimal(row.reorder_level) },
    ],
  },
  {
    key: "material-consumption",
    label: "Material Consumption",
    dateFiltered: true,
    columns: [
      { key: "period", label: "Period", format: "date", render: (row) => formatReportDate(row.period) },
      { key: "qty_consumed", label: "Qty consumed", format: "number", render: (row) => formatDecimal(row.qty_consumed) },
      { key: "cost_consumed", label: "Cost consumed", format: "currency", render: (row) => `${getVendorCurrency()} ${formatDecimal(row.cost_consumed)}` },
    ],
  },
  {
    key: "variance",
    label: "Inventory Variance",
    dateFiltered: false,
    columns: [
      { key: "booking_id", label: "Booking" },
      { key: "item_name", label: "Item" },
      { key: "sku", label: "SKU" },
      { key: "parts_qty", label: "Parts used qty", format: "number", render: (row) => formatDecimal(row.parts_qty) },
      { key: "txn_qty", label: "Ledger deducted qty", format: "number", render: (row) => formatDecimal(row.txn_qty) },
      {
        key: "variance_type",
        label: "Status",
        render: (row) => (
          <Badge bg={VARIANCE_BADGE[row.variance_type] || "secondary"}>
            {VARIANCE_LABEL[row.variance_type] || row.variance_type}
          </Badge>
        ),
      },
    ],
  },
  {
    key: "adjustment-logs",
    label: "Adjustment Logs",
    dateFiltered: true,
    columns: [
      { key: "created_at", label: "Date", format: "date", render: (row) => formatReportDate(row.created_at) },
      { key: "item_name", label: "Item" },
      { key: "sku", label: "SKU" },
      { key: "txn_type", label: "Type" },
      { key: "qty_change", label: "Qty change", format: "number", render: (row) => formatDecimal(row.qty_change) },
      { key: "balance_after", label: "Balance after", format: "number", render: (row) => formatDecimal(row.balance_after) },
      { key: "reason", label: "Reason" },
      { key: "performed_by_name", label: "Performed by" },
    ],
  },
];

function InventoryReports() {
  const [activeKey, setActiveKey] = useState(REPORTS[0].key);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const activeReport = REPORTS.find((r) => r.key === activeKey);
  const dateRangeInvalid = Boolean(activeReport.dateFiltered && from && to && from > to);

  const load = useCallback(async () => {
    if (dateRangeInvalid) return;
    setLoading(true);
    try {
      const res = await inventoryService.getReport(
        activeKey,
        activeReport.dateFiltered ? { from, to } : {}
      );
      setRows(res?.data || []);
    } catch (err) {
      toast.error(err.message || "Could not load report");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, from, to, dateRangeInvalid]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="p-3">
      <style>{`
        .inv-refresh-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 8px;
          border: 1.5px solid #E8EAF0;
          background: #fff;
          color: #475569;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
        }
        .inv-refresh-btn:hover:not(:disabled) {
          background: #F8F9FC;
          border-color: #CBD5E1;
          color: #0F172A;
        }
        .inv-refresh-btn:disabled {
          opacity: 0.6;
          cursor: default;
        }
      `}</style>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="mb-0">Inventory Reports</h4>
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="inv-refresh-btn" onClick={load} disabled={loading}>
            ↻ Refresh
          </button>
          <ExportMenu
            rows={rows}
            columns={activeReport.columns}
            filenameBase={activeReport.key}
            onExportPdf={(html) => inventoryService.exportReportPdf(activeReport.key, html)}
          />
        </div>
      </div>

      {activeReport.dateFiltered && (
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div className="d-flex gap-2 flex-wrap align-items-end">
            <Form.Group>
              <Form.Label style={{ fontSize: 12 }}>From</Form.Label>
              <Form.Control
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label style={{ fontSize: 12 }}>To</Form.Label>
              <Form.Control
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
              />
            </Form.Group>
          </div>
          {dateRangeInvalid && (
            <div style={{ color: "var(--button-color)", fontSize: 12, marginTop: 8 }}>
              From date must be before To date.
            </div>
          )}
        </div>
      )}

      <div className="d-flex gap-2 mb-3 flex-wrap" style={{ background: "#fff", borderRadius: "12px", padding: "8px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
        {REPORTS.map((r) => (
          <button
            key={r.key}
            type="button"
            style={tabBtnStyle(activeKey === r.key)}
            onClick={() => setActiveKey(r.key)}
          >
            {r.label}
            {!r.dateFiltered && (
              <Badge bg="success" style={{ marginLeft: 6, fontSize: 9, verticalAlign: "middle" }}>
                Live
              </Badge>
            )}
          </button>
        ))}
      </div>

      <InventoryTable
        columns={activeReport.columns}
        data={dateRangeInvalid ? [] : rows}
        loading={loading}
        showSearch={false}
        emptyMessage={dateRangeInvalid ? "Fix the date range above to view this report." : "No data for this report yet."}
      />
    </div>
  );
}

export default InventoryReports;
